import { Component, OnInit, OnDestroy } from '@angular/core';
import { SessionService } from '@app/security/session.service';
import { FeatureService } from '@app/core/services/feature.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { combineLatest, Subscription } from 'rxjs';

const DISMISSED_KEY = 'outage-modal-dismissed';

export const DEFAULT_OUTAGE_TITLE = 'Temporary Service Issue';
export const DEFAULT_OUTAGE_MESSAGE =
  "Something isn't working as expected right now. Our team is actively " +
  'investigating and working to restore full service as quickly as possible. ' +
  'Thank you for your patience.';

// Guards against a pasted wall of text breaking the layout. Longer values are
// truncated, not rejected, so the notice still appears.
const MAX_TITLE_LENGTH = 120;
const MAX_MESSAGE_LENGTH = 1000;

interface OutageNotice {
  title: string;
  message: string;
}

/**
 * Resolves the notice to show from the flag set, or null when none is active.
 *
 * `outage-notice` is a JSON flag, `{ enabled, title?, message? }`, so support
 * can put up and change the wording from LaunchDarkly without a deploy. The
 * original boolean `outage-modal` flag still works and shows the default text.
 * LaunchDarkly cannot change a flag's type in place, which is why the JSON
 * flag is a new key rather than a change to the old one.
 *
 * Text is only ever bound with interpolation (never innerHTML), so any markup
 * in the flag renders as literal text.
 */
function resolveNotice(flags: Record<string, unknown>): OutageNotice | null {
  const configured = flags[FeatureFlags.outage_notice];
  if (isRecord(configured) && configured['enabled'] === true) {
    return {
      title: textOrDefault(
        configured['title'],
        DEFAULT_OUTAGE_TITLE,
        MAX_TITLE_LENGTH,
      ),
      message: textOrDefault(
        configured['message'],
        DEFAULT_OUTAGE_MESSAGE,
        MAX_MESSAGE_LENGTH,
      ),
    };
  }

  if (flags[FeatureFlags.outage_modal] === true) {
    return { title: DEFAULT_OUTAGE_TITLE, message: DEFAULT_OUTAGE_MESSAGE };
  }

  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function textOrDefault(
  value: unknown,
  fallback: string,
  maxLength: number,
): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return fallback;
  }
  // Array.from splits on code points, so an emoji at the cut-off is not cut
  // in half.
  return Array.from(value.trim()).slice(0, maxLength).join('');
}

// What a dismissal is remembered by: the wording itself. sessionStorage holds
// at most the title and message limits (about 1,100 characters), so there is no
// need for a hash, and no chance of a collision showing or hiding the wrong
// notice. Not sensitive: it is text the user has already been shown.
function fingerprint(notice: OutageNotice): string {
  return JSON.stringify([notice.title, notice.message]);
}

@Component({
  selector: 'outage-modal',
  templateUrl: './outage-modal.component.html',
  styleUrls: ['./outage-modal.component.scss'],
  standalone: true,
})
export class OutageModalComponent implements OnInit, OnDestroy {
  showModal = false;
  title = DEFAULT_OUTAGE_TITLE;
  message = DEFAULT_OUTAGE_MESSAGE;
  private modalStateSub: Subscription;
  private hasLoggedIn = false;
  private currentFingerprint: string | null = null;

  constructor(
    private sessionService: SessionService,
    private featureService: FeatureService,
  ) {}

  ngOnInit(): void {
    this.modalStateSub = combineLatest([
      this.sessionService.loggedIn$,
      this.featureService.getFlags(),
    ]).subscribe(([loggedIn, flags]) => {
      if (!loggedIn) {
        if (this.hasLoggedIn) {
          sessionStorage.removeItem(DISMISSED_KEY);
        }
        this.hasLoggedIn = false;
        this.hideModal();
        return;
      }

      this.hasLoggedIn = true;
      const notice = flags != null ? resolveNotice(flags) : null;

      if (!notice) {
        this.hideModal();
        return;
      }

      // A dismissal only covers the wording that was dismissed, so changing
      // the text mid-incident shows the update to people who closed the old one.
      const noticeFingerprint = fingerprint(notice);
      if (this.wasDismissed(noticeFingerprint)) {
        this.hideModal();
        return;
      }

      this.showOutageModal(notice, noticeFingerprint);
    });
  }

  ngOnDestroy(): void {
    this.modalStateSub?.unsubscribe();
    this.hideModal();
  }

  dismiss(): void {
    this.hideModal();
    if (this.currentFingerprint) {
      sessionStorage.setItem(DISMISSED_KEY, this.currentFingerprint);
    }
  }

  private wasDismissed(noticeFingerprint: string): boolean {
    return sessionStorage.getItem(DISMISSED_KEY) === noticeFingerprint;
  }

  private showOutageModal(
    notice: OutageNotice,
    noticeFingerprint: string,
  ): void {
    this.title = notice.title;
    this.message = notice.message;
    this.currentFingerprint = noticeFingerprint;

    if (this.showModal) {
      return;
    }

    this.showModal = true;
    this.preventBodyScroll();
  }

  private hideModal(): void {
    if (!this.showModal) {
      return;
    }

    this.showModal = false;
    this.allowBodyScroll();
  }

  private preventBodyScroll(): void {
    document.body.classList.add('body--fixed');
  }

  private allowBodyScroll(): void {
    document.body.classList.remove('body--fixed');
  }
}
